import axios from "axios";
import "./interceptors";

/**
 * Upload a File to S3 through a server-issued presigned URL and return the
 * stored key (the value to persist on the owning record).
 *
 * This replaces the legacy raw-IAM-credential upload (`react-s3` +
 * `getAwsCredentials`), which shipped the bucket's access keys to the browser.
 * The new API deliberately never exposes them, so `getAwsCredentials` is a
 * stub returning `false` — which is why every form still built on it (Create
 * Headline) silently produced a null media key and then failed validation
 * server-side. Mirrors the mobile client's proven flow
 * (mobile/app/services/media/uploadFile.ts).
 *
 * `kind` must be one of the API's UploadKind values:
 * profile | audio | video | poster | contest-song — it picks the S3 namespace.
 *
 * The S3 PUT uses `fetch`, NOT axios: this app's axios interceptor rewrites
 * baseURL and replaces the headers with an `authorization` bearer on EVERY
 * request, and sending that to S3 breaks the presigned signature.
 */
/**
 * Size ceilings, matching the mobile client so a producer and a staff member are
 * held to the same contract. The admin had none at all: a large file went
 * straight to a presigned PUT that expires in 15 minutes, and on a slow
 * connection it simply ran out of URL with a bare "Upload failed (403)".
 */
const MAX_BYTES = { profile: 10 * 1024 * 1024, poster: 10 * 1024 * 1024, video: 60 * 1024 * 1024, audio: 50 * 1024 * 1024, "contest-song": 50 * 1024 * 1024 };

/** Which media family each kind carries — the API now rejects a mismatch. */
const FAMILY = { profile: "image", poster: "image", video: "video", audio: "audio", "contest-song": "audio" };

/** Extension → MIME, for the browsers that report an empty `File.type`. */
const MIME_BY_EXT = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic", gif: "image/gif",
  mp4: "video/mp4", mov: "video/quicktime", m4v: "video/mp4", webm: "video/webm",
  mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4", aac: "audio/aac", flac: "audio/flac",
};

/**
 * The content type to declare for a picked file.
 *
 * `File.type` is empty whenever the browser cannot infer one from the extension,
 * and the API's presign validates `^(image|audio|video)/…` — so an empty string
 * is rejected. That used to surface as a raw validation error with nothing
 * saying which file caused it. Falls back to the extension, and finally to the
 * kind's own family so the request is at least well-formed.
 */
function contentTypeFor(file, kind) {
  if (file.type) return file.type;
  const ext = (file.name || "").split(".").pop().toLowerCase();
  return MIME_BY_EXT[ext] || `${FAMILY[kind] || "image"}/octet-stream`;
}

export async function uploadViaPresign(file, kind) {
  const contentType = contentTypeFor(file, kind);

  // Say WHICH rule the file broke, before spending the upload. The API rejects a
  // kind/type mismatch too, but catching it here names the file rather than
  // returning a schema error about a field the person never saw.
  const family = FAMILY[kind];
  if (family && contentType.split("/")[0] !== family) {
    throw new Error(`"${file.name}" is not a ${family} file.`);
  }
  const max = MAX_BYTES[kind];
  if (max && file.size > max) {
    throw new Error(
      `"${file.name}" is ${Math.round(file.size / (1024 * 1024))}MB — the limit is ${Math.round(max / (1024 * 1024))}MB.`,
    );
  }

  const res = await axios.post("api/v1/media/upload-url", {
    kind,
    contentType,
  });
  const target = res && res.data;
  if (!target || !target.uploadUrl || !target.key) {
    throw new Error("Could not get an upload URL");
  }

  const put = await fetch(target.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  });
  if (!put.ok) {
    throw new Error(`Upload failed (${put.status})`);
  }

  // Image kinds get their sized variants generated at save time. Best-effort:
  // the original is already in S3, so a failure here only means no thumbnails.
  if (kind === "profile" || kind === "poster") {
    try {
      await axios.post("api/v1/media/process", { key: target.key, kind });
    } catch {
      // Non-fatal — the on-the-fly variant fallback still serves the image.
    }
  }

  // Stored keys are persisted with a leading slash, matching how the API's
  // asset resolver builds absolute URLs from them.
  return `/${target.key.replace(/^\/+/, "")}`;
}
